import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2, LogOut, FileText, ShieldAlert, Building2, LogIn } from "lucide-react";

const TOKEN_KEY = "bi_portal_token";
const SNAP = [
  ["stato_piva", "Stato P.IVA"], ["forma_giuridica", "Forma giuridica"], ["sede", "Sede"],
  ["ateco", "ATECO"], ["settore", "Settore"], ["pec", "PEC"], ["sito", "Sito web"],
];

export default function BiPortal() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [me, setMe] = useState(null);
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [model, setModel] = useState("gpt");
  const [d, setD] = useState(null);

  const authAxios = useCallback((extra = {}) => axios.create({
    baseURL: API, headers: { Authorization: `Bearer ${token}`, ...extra },
  }), [token]);

  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setMe(null); setD(null); }, []);

  useEffect(() => {
    if (!token) return;
    authAxios().get("/abi/portal/me").then(({ data }) => setMe(data)).catch(() => logout());
  }, [token, authAxios, logout]);

  const login = async () => {
    if (!creds.username || !creds.password) { toast.error("Inserisci username e password"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/abi/portal/login`, creds);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token); setMe(data);
      toast.success(`Benvenuto${data.label ? ", " + data.label : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const analyze = async () => {
    if (!query.trim()) { toast.error("Inserisci P.IVA, C.F. o Ragione Sociale"); return; }
    setBusy(true); setD(null);
    try {
      const { data } = await authAxios().post("/abi/portal/analyze", { query: query.trim() });
      setD(data);
      authAxios().get("/abi/portal/me").then(({ data: m }) => setMe(m)).catch(() => {});
    } catch (e) {
      const st = e.response?.status;
      if (st === 401) logout();
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const report = async () => {
    try {
      const { data: blob } = await authAxios().get("/abi/portal/report/pdf", {
        params: { analysis_id: d.id, model }, responseType: "blob",
      });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Errore generazione report"); }
  };

  if (!token || !me) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 px-4" data-testid="bi-portal-login">
        <div className="w-full max-w-md rounded-2xl bg-white/95 backdrop-blur p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-1"><Building2 className="h-6 w-6 text-violet-600" />
            <h1 className="font-heading text-xl font-semibold text-slate-900">Business Intelligence</h1></div>
          <p className="text-sm text-slate-500 mb-6">Portale riservato — accedi con le credenziali fornite dall'agenzia.</p>
          <label className="text-xs text-slate-500 block mb-1">Username</label>
          <Input value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} className="mb-3" data-testid="bi-portal-username" />
          <label className="text-xs text-slate-500 block mb-1">Password</label>
          <Input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && login()} className="mb-5" data-testid="bi-portal-password" />
          <Button onClick={login} disabled={busy} className="w-full bg-violet-600 hover:bg-violet-700" data-testid="bi-portal-login-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <LogIn className="h-4 w-4 mr-1.5" />} Accedi</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="bi-portal">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-violet-600" />
            <span className="font-heading font-semibold text-slate-900">Business Intelligence</span></div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-500">{me.label || me.username}{me.quota_mese != null ? ` · ${me.rimanenti} analisi rimaste` : ""}</span>
            <Button size="sm" variant="ghost" onClick={logout} data-testid="bi-portal-logout"><LogOut className="h-4 w-4 mr-1" /> Esci</Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="rounded-xl border border-slate-200 bg-white p-4 mb-6 flex gap-2" data-testid="bi-portal-search">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && analyze()}
            placeholder="Partita IVA, Codice Fiscale o Ragione Sociale" className="h-11" data-testid="bi-portal-query" />
          <Button onClick={analyze} disabled={busy} className="h-11 bg-violet-600 hover:bg-violet-700" data-testid="bi-portal-analyze">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}</Button>
        </div>

        {busy && !d && <div className="p-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Analisi in corso…</div>}

        {d && (
          <div className="space-y-4" data-testid="bi-portal-result">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h2 className="font-heading text-lg font-semibold text-slate-900">{d.denominazione || d.query}</h2>
              <p className="text-xs text-slate-500 mb-3">{(d.tipo_input || "").toUpperCase()}: {d.identificativo || d.query}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                {SNAP.map(([k, label]) => (
                  <div key={k} className="flex justify-between border-b border-slate-50 pb-1">
                    <span className="text-slate-500">{label}</span>
                    <span className="text-slate-800 text-right">{d.snapshot?.[k] || <span className="text-slate-300 italic">n/d</span>}</span>
                  </div>
                ))}
              </div>
            </div>

            {d.catnat && (
              <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-5" data-testid="bi-portal-catnat">
                <div className="flex items-center gap-2 mb-2"><ShieldAlert className="h-4 w-4 text-orange-600" />
                  <h3 className="font-heading font-semibold text-slate-800 text-sm">Rischio catastrofale (CAT NAT)</h3></div>
                <p className="text-xs text-slate-500 mb-2">{d.catnat.comune}{d.catnat.provincia ? ` (${d.catnat.provincia})` : ""}</p>
                <div className="flex gap-2 flex-wrap">
                  <span className="text-xs px-2 py-1 rounded bg-white border border-emerald-300">Sisma: <b>{d.catnat.rischio_sismico}</b>{d.catnat.sismico_fonte ? " ✓" : ""}</span>
                  <span className="text-xs px-2 py-1 rounded bg-white border border-orange-200">Alluvione: <b>{d.catnat.rischio_alluvione}</b> (stima)</span>
                </div>
                {d.catnat.consiglio && <p className="text-xs text-orange-800 mt-2 font-medium">{d.catnat.consiglio}</p>}
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-2">
              <span className="text-xs text-slate-500">Relazione con:</span>
              <select value={model} onChange={(e) => setModel(e.target.value)} className="h-9 text-sm border border-slate-200 rounded px-2" data-testid="bi-portal-model">
                <option value="gpt">GPT-5.4</option>
                <option value="claude">Claude Sonnet 4.6</option>
              </select>
              <Button onClick={report} className="ml-auto bg-emerald-600 hover:bg-emerald-700" data-testid="bi-portal-report"><FileText className="h-4 w-4 mr-1.5" /> Scarica report PDF</Button>
            </div>
            <p className="text-[11px] text-slate-400 text-center">I dati aggregano fonti pubbliche gratuite e stime automatiche: non costituiscono visura ufficiale né consulenza.</p>
          </div>
        )}
      </main>
    </div>
  );
}
